import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import ClonalityHeatmapCell from './ClonalityHeatmapCell';
import { symmetricKey } from './symmetricKey';

const REGULAR_CELL_SIZE = 36;
const MIN_ROW_HEADER_WIDTH = 140;
const MIN_HEADER_HEIGHT = 72;
const MAX_HEADER_HEIGHT = 240;
const CAPTION_CHAR_WIDTH_PX = 7;
const HEADER_PADDING_PX = 16;
const MAX_LABEL_CHARS = 10;
const DENSE_SAMPLE_THRESHOLD = 25;
const ULTRA_DENSE_SAMPLE_THRESHOLD = 35;
const DENSE_HEADER_PADDING_PX = '2px';
const DEFAULT_HEADER_PADDING_X = 0.5;
const DEFAULT_ROW_HEADER_PADDING = 1;
const DENSE_COLUMN_WIDTH = 14;
const DENSE_ROW_HEADER_WIDTH = 90;
const ULTRA_DENSE_COLUMN_WIDTH = 16;

const truncateWithEllipsis = (value, maxChars) => {
  if (value.length <= maxChars) {
    return value;
  }
  return `${value.slice(0, maxChars - 3)}...`;
};

function ClonalityHeatmap({
  samples,
  comparisons,
  clusters
}) {
  const longestNameLength = useMemo(
    () => samples.reduce((max, sample) => Math.max(max, Math.min(sample.name.length, MAX_LABEL_CHARS)), 0),
    [samples]
  );

  const headerHeight = useMemo(() => {
    const estimated = Math.ceil(
      (longestNameLength * CAPTION_CHAR_WIDTH_PX) + HEADER_PADDING_PX
    );
    return Math.min(MAX_HEADER_HEIGHT, Math.max(MIN_HEADER_HEIGHT, estimated));
  }, [longestNameLength]);

  const isDense = samples.length > DENSE_SAMPLE_THRESHOLD;
  const isUltraDense = samples.length > ULTRA_DENSE_SAMPLE_THRESHOLD;
  const rowHeaderWidth = isDense
    ? DENSE_ROW_HEADER_WIDTH
    : Math.max(MIN_ROW_HEADER_WIDTH, headerHeight);
  const cellSize = isUltraDense
    ? ULTRA_DENSE_COLUMN_WIDTH
    : isDense
      ? DENSE_COLUMN_WIDTH
      : REGULAR_CELL_SIZE;
  const colHeaderPaddingX = isDense ? DENSE_HEADER_PADDING_PX : DEFAULT_HEADER_PADDING_X;
  const rowHeaderPadding = isDense ? DENSE_HEADER_PADDING_PX : DEFAULT_ROW_HEADER_PADDING;

  const dataColumnTemplate = `repeat(${samples.length}, ${cellSize}px)`;
  const gridTemplateColumns = isUltraDense
    ? dataColumnTemplate
    : `${rowHeaderWidth}px ${dataColumnTemplate}`;

  const labelBySampleId = useMemo(() => {
    const labels = new Map();
    clusters.forEach((cluster) => {
      if (cluster.size < 2 || cluster.sampleIds.length === 0 || cluster.label == null) {
        return;
      }
      cluster.sampleIds.forEach((sampleId) => {
        labels.set(sampleId, cluster.label);
      });
    });
    return labels;
  }, [clusters]);

  return (
    <Box
      role="grid"
      sx={{
        display: 'grid',
        gridTemplateColumns,
        gap: '1px',
        backgroundColor: 'divider',
        width: 'max-content'
      }}
    >
      {!isDense && (
        <>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              left: 0,
              zIndex: 4,
              backgroundColor: 'background.paper',
              height: headerHeight,
              minWidth: rowHeaderWidth
            }}
          />
          {samples.map((sample) => {
            const clusterLabel = labelBySampleId.get(sample.id);
            const displayName = truncateWithEllipsis(sample.name, MAX_LABEL_CHARS);

            return (
              <Box
                key={`col-${sample.id}`}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                  height: headerHeight,
                  width: cellSize,
                  minWidth: cellSize,
                  maxWidth: cellSize,
                  boxSizing: 'border-box',
                  px: colHeaderPaddingX,
                  overflow: 'visible'
                }}
              >
                <Tooltip title={sample.name}>
                  <Box>
                    <Typography
                      variant="caption"
                      aria-label={sample.name}
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: '50%',
                        transformOrigin: 'left bottom',
                        transform: 'rotate(-75deg)',
                        whiteSpace: 'nowrap',
                        lineHeight: 1
                      }}
                    >
                      {clusterLabel && (
                        <Box component="span" sx={{ fontWeight: 700, mr: 0.25 }}>
                          {`(${clusterLabel})`}
                        </Box>
                      )}
                      {displayName}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
        </>
      )}

      {samples.map((rowSample) => (
        <React.Fragment key={`row-${rowSample.id}`}>
          {!isUltraDense && (
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper',
                height: cellSize,
                minWidth: rowHeaderWidth,
                maxHeight: cellSize,
                boxSizing: 'border-box',
                px: rowHeaderPadding,
                py: isDense ? 0 : rowHeaderPadding,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden'
              }}
            >
              <Typography variant="caption" noWrap>
                {labelBySampleId.has(rowSample.id) && (
                  <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                    {`(${labelBySampleId.get(rowSample.id)})`}
                  </Box>
                )}
                {rowSample.name}
              </Typography>
            </Box>
          )}
          {samples.map((colSample) => {
            const isDiagonal = rowSample.id === colSample.id;
            const pairKey = symmetricKey(rowSample.id, colSample.id);
            const entry = comparisons.get(pairKey);
            const value = isDiagonal ? 0 : (entry?.value ?? null);
            const skipReason = isDiagonal ? null : (entry?.skipReason ?? null);

            return (
              <ClonalityHeatmapCell
                key={`${rowSample.id}-${colSample.id}`}
                rowSample={rowSample}
                colSample={colSample}
                value={value}
                skipReason={skipReason}
                isDiagonal={isDiagonal}
                hideValueText={isDense}
                cellSize={cellSize}
              />
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
}

ClonalityHeatmap.propTypes = {
  samples: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  comparisons: PropTypes.instanceOf(Map).isRequired,
  clusters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    kind: PropTypes.oneOf(['clonal', 'singleton']),
    label: PropTypes.string,
    sampleIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    size: PropTypes.number.isRequired
  })).isRequired
};

export { symmetricKey };
export default ClonalityHeatmap;
